import { BotType, DexSniperBot } from 'api/types/httpsTypes/dex-bot';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import { BotsRow } from './BotsRow';
import Button from 'modules/shared-components/button/button';
import { Link } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { desktopColumns, mobileColumns } from './columns';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { BotsSortKeys, useBotsSearchFilters } from './usePageParams';
import { Tabs } from '../../components/layout/SIDEBAR_CONST';
import {
  SortDropdown,
  SortDropdownProps,
} from '../../components/dropdown/sort-dropdown';
import { Pagination } from 'api/types/httpsTypes';
import { forwardRef, Ref } from 'react';

export function thDynamicClass(s: string) {
  return s === 'BOT' ? 'col-span-2' : '';
}

interface Props {
  items: DexSniperBot[] | [];
  pagination: Pagination | undefined;
  ref: Ref<HTMLTableRowElement>;
  botType: BotType;
}

export const SORT_OPTIONS = [
  {
    label: 'Creation Date',
    value: `-${BotsSortKeys.CreatedAt}`,
  },
  {
    label: 'Name',
    value: `-${BotsSortKeys.Name}`,
  },
  {
    label: 'Status',
    value: `${BotsSortKeys.Status}`,
  },
  {
    label: '30D PnL',
    value: `-${BotsSortKeys.PnlDollar}`,
  },
  {
    label: '30D Volume',
    value: `-${BotsSortKeys.VolumeDollar}`,
  },
];

export function Sort(props: Omit<SortDropdownProps<string>, 'options'>) {
  return (
    <SortDropdown
      size="xs"
      className="xxs:p-2 h-auto"
      {...props}
      options={SORT_OPTIONS}
    />
  );
}

export const BotsMainTable = forwardRef<HTMLTableRowElement, Props>(
  ({ items, pagination, botType }, ref) => {
    const isLargeScreen = useMediaQuery(screenGte.large);
    const { filtersState, setSort } = useBotsSearchFilters();

    const tab =
      botType === BotType.Volatility
        ? Tabs.VolatilityBot
        : botType === BotType.Wallet
        ? Tabs.WalletCopyTrading
        : Tabs.TgCopyTrading;

    const isSortAscending = !filtersState.botSort?.includes('-');

    const columns = isLargeScreen
      ? desktopColumns({ botType })
      : mobileColumns({ botType });

    return (
      <div>
        {items.length > 0 ? (
          <div>
            <div className="w-full lg:min-w-[1000px]">
              <div className="lg:hidden xxs:mx-2 xxs:mb-2">
                <Sort
                  value={filtersState.botSort}
                  onChange={(value) => {
                    setSort(value);
                  }}
                />
              </div>

              <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                <Header
                  trClassName={`lg:grid lg:grid-cols-11 lg:dark:bg-dex-black-800 border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-700`}
                  columns={columns}
                  thDynamicClass={thDynamicClass}
                  rowClassName="xxs:p-1"
                  isSortAscending={isSortAscending}
                  sortBy={filtersState.botSort?.replace('-', '')}
                  className="lg:dark:bg-dex-black-700 xxs:text-xs lg:grid"
                  onClickColumn={(_, sortableKey) => {
                    if (sortableKey) setSort(sortableKey);
                  }}
                />
                <tbody className="lg:block lg:h-terminal overflow-y-auto">
                  <>
                    {items?.map((item, rowId) => (
                      <BotsRow
                        key={rowId}
                        botType={botType}
                        data={item}
                        columnsDef={columns}
                      />
                    ))}
                  </>
                  {pagination && pagination.total > items.length && (
                    <Table.LoadingRow
                      ref={ref}
                      cellClassName="xxs:py-3"
                      elementClassName="xxs:dark:bg-dex-black-700 "
                      className={`lg:grid lg:grid-cols-11 lg:dark:bg-dex-black-800 `}
                      columns={columns}
                    />
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <div>
            <div className="lg:min-h-[600px] h-full flex justify-center items-center">
              <div className="flex flex-col items-center space-y-2">
                {botType === BotType.Wallet ? (
                  <div>Create your first bot and copy-trade other wallets.</div>
                ) : botType === BotType.Volatility ? (
                  <div>
                    Create your first bot and trade based on volatility.
                  </div>
                ) : (
                  <div>
                    Create your first bot and copy-trade hot Telegram Channels.
                  </div>
                )}
                <div className="flex w-full lg:w-auto">
                  <Button
                    as={Link}
                    to={`/dex/bots/${tab}/new`}
                    variant="dexNeutral"
                    size="large"
                    className="xxs:text-base lg:text-xs m-0 xxs:p-1 flex space-x-2 lg:space-x-1 normal-case w-full lg:w-auto xxs:text-sm"
                  >
                    <PlusIcon className="h-5 w-5 lg:h-4 lg:w-4" />
                    <div>
                      New{' '}
                      {botType === BotType.Wallet
                        ? 'Wallet Copy'
                        : botType === BotType.Volatility
                        ? 'Volatility'
                        : 'Telegram Calls'}{' '}
                      Bot
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);
